/**
 * Waits for Clerk to finish bootstrapping before token-dependent code concludes
 * there is no session.
 *
 * On a cold start / resume the `window.Clerk` singleton can exist while its
 * `session` is still being rehydrated. Code that needs a token must not treat
 * that transient window as "signed out" — otherwise it serves a stale cached
 * token and triggers a false 401 sign-out (see logout-telemetry). This helper
 * lets such code await readiness, bounded by a timeout, and never rejects.
 */

const DEFAULT_TIMEOUT_MS = 15_000;
const POLL_INTERVAL_MS = 100;

// Terminal Clerk statuses: once reached, waiting longer won't change the outcome.
type ClerkStatus = "loading" | "ready" | "error" | "degraded";

type ClerkGlobal = {
  loaded?: boolean;
  status?: ClerkStatus;
  session?: unknown;
  addListener?: (callback: () => void) => () => void;
};

const getClerk = (): ClerkGlobal | undefined =>
  (window as unknown as { Clerk?: ClerkGlobal }).Clerk;

const isClerkReady = (clerk: ClerkGlobal | undefined): boolean => {
  if (!clerk) return false;
  if (clerk.status) return clerk.status !== "loading";
  return clerk.loaded === true;
};

export type ClerkReadiness = "ready" | "unavailable";

const readiness = (): ClerkReadiness => {
  const clerk = getClerk();
  if (clerk?.status && clerk.status !== "ready") return "unavailable";
  return clerk?.status === "ready" || clerk?.loaded === true ? "ready" : "unavailable";
};

/**
 * Resolves once Clerk has finished bootstrapping (or after `timeoutMs`).
 * Returns unavailable for timeout/error/degraded states; callers must not
 * interpret those outcomes as proof that the session ended. Never rejects.
 */
export const waitForClerkReady = (
  timeoutMs: number = DEFAULT_TIMEOUT_MS,
): Promise<ClerkReadiness> => {
  if (isClerkReady(getClerk())) return Promise.resolve(readiness());

  return new Promise<ClerkReadiness>((resolve) => {
    let settled = false;
    let unsubscribe: (() => void) | null = null;
    let poll: ReturnType<typeof setInterval> | null = null;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const finish = () => {
      if (settled) return;
      settled = true;
      if (unsubscribe) unsubscribe();
      if (poll !== null) clearInterval(poll);
      if (timer !== null) clearTimeout(timer);
      resolve(readiness());
    };

    // The listener fires on Clerk resource changes, but it can only be attached
    // once `window.Clerk` exists. The poll covers the pre-injection window and
    // wires up the listener as soon as Clerk appears.
    poll = setInterval(() => {
      const clerk = getClerk();
      if (isClerkReady(clerk)) {
        finish();
        return;
      }
      if (clerk?.addListener && !unsubscribe) {
        unsubscribe = clerk.addListener(() => {
          if (isClerkReady(getClerk())) finish();
        });
      }
    }, POLL_INTERVAL_MS);

    timer = setTimeout(finish, timeoutMs);
  });
};
